<script setup lang="ts">
/**
 * Appoint & Withdraw Director (동시) — SG 공통 프리셋 **스튜디오** 패널(문서 재현).
 *   Stage 1 DR ×1 (현직 이사 서명) · Stage 2 Form 45 ×선임수 · Stage 3 Resignation Letter ×사임수.
 *   cdd(문서 재현 입력엔진)·서명자·DocScope 는 aura/agm 재사용.
 */
import { ref, computed, watch, onMounted, provide } from 'vue';
import { useRouter } from 'vue-router';
import { Icon } from '@iconify/vue';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import type { OfficerDetail } from '@/features/AI-documents/types/docGen.types';
import type { SignerEntry } from '@/features/AI-documents/types/docGen.types';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { fetchSessionClientNm } from '@/features/startTask/preset_task/sg/composables/fetchSessionClientNm';
import { provideAgmFill, bareKey, scopeOf } from '@/features/startTask/my_preset/sg/aura/agm/cdd/useCddFill';
import { provideAgmSigners, AGM_ENTITY_CD } from '@/features/startTask/my_preset/sg/aura/agm/cdd/agmSignersContext';
import { buildDirectorsSignersJson } from '@/features/startTask/preset_task/sg/components/allotment-shares/composables/allotmentSigners';
import { getActiveDirectors } from '@/features/startTask/preset_task/sg/components/sgDocGenEntityApi';
import { buildAppointedOfficerSignerJson } from '@/features/startTask/preset_task/sg/components/appoint-director/composables/appointDirectorSigners';
import { buildSgPresetPrepareDocumentItem } from '@/features/startTask/preset_task/sg/components/presetDocCustSave';
import { directorIdLabel } from '@/features/startTask/preset_task/sg/components/appoint-director/composables/directorIdLabel';
import { buildContactDataFromSelectedPerson } from '@/features/startTask/preset_task/sg/components/appoint-director/composables/appointDirectorContact';
import { applyDirectorStructuredProfileFromContactData, createEmptyAppointDirectorForm } from '@/features/startTask/preset_task/sg/components/appoint-director/composables/useFormPayload';
import { baseCodeApi } from '@/shared/api/baseCodeApi';
import AppointWithdrawPackageDoc from '@/features/startTask/preset_task/sg/components/appoint-withdraw-director/studio/AppointWithdrawPackageDoc.vue';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: Record<string, any> | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  activeAddressContext?: Record<string, any> | null;
  editTaskId?: number | null;
  studio?: boolean;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'request-person-lookup', context: Record<string, any>): void;
  (e: 'request-address-lookup', context: Record<string, any>): void;
  (e: 'progress-change', pct: number): void;
}>();

const router = useRouter();
const sessionStore = useSessionStore();
const fill = provideAgmFill();
const signersCtx = provideAgmSigners();
const creating = ref(false);
const progress = ref('');
const taskConfigSteps = ref<string[]>([]);
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; });
watch(progress, (v) => { presetProgress.message = v; });

/** 문서 필드 채움율 → 스튜디오 상단 프로그레스바(%) 갱신. incorporation-pre 패턴 재사용. */
function emitProgress() {
  const total = fill.totalFields.value || 1;
  const filled = fill.filledCount.value;
  const pct = Math.min(100, Math.round((filled / total) * 100));
  emit('progress-change', pct);
}
watch([() => fill.filledCount.value, () => fill.totalFields.value], emitProgress, { immediate: true });

const studio = computed(() => !!props.studio);
const editMode = computed(() => !!props.editTaskId);
const effectiveTaskTitle = computed(() => (props.taskTitle || '').trim());
const docRef = ref<InstanceType<typeof AppointWithdrawPackageDoc> | null>(null);
const autoLocal = ref<{ newIds: number[]; resignIds: number[] } | null>(null);
const lastAutofillCd = ref('');

const entityCd = computed(() => String(props.entityData?.icpCd || props.entityData?.entityCd || '').trim());
provide(AGM_ENTITY_CD, entityCd);

const st = (v: unknown) => String(v ?? '').trim();
let signerSeq = 0;
function mkSigner(name: string, role = 'director'): SignerEntry {
  return { id: `awd-${signerSeq++}`, name: st(name), title: 'Director', role, isManual: true, showTitle: true, _signerLayout: 'box' } as SignerEntry;
}

// 회사 정보 자동채움 + DR 서명자(현직 이사 전원) 시드.
async function autofill(ent: Record<string, any> | null) {
  if (!ent) return;
  const cd = entityCd.value;
  if (cd && cd === lastAutofillCd.value) return;
  lastAutofillCd.value = cd;
  const set = (k: string, val: unknown) => { const s = st(val); if (s) fill.setValue(k, s); };
  set('company_name', ent.companyName || ent.entityName || ent.ENTITY_NAME);
  set('company_registration_no', ent.registrationNo || ent.regNo || ent.REG_NO);
  set('company_address', ent.registeredOfficeAddress || ent.address || ent.companyAddress);
  // 회사 등록사무소 주소·UEN — entityData 에 없으면 agm-source 에서 보완.
  if (cd) {
    try {
      const res: any = await fetch(`/api/entity/${encodeURIComponent(cd)}/agm-source`, { credentials: 'include' }).then((r) => r.json());
      const src: any = res?.data?.data ?? res?.data ?? res ?? {};
      if (!st(fill.values.company_address)) set('company_address', src.registeredOfficeAddress);
      if (!st(fill.values.company_registration_no)) set('company_registration_no', src.registrationNo);
    } catch { /* ignore */ }
  }
  // 비서(lodger) = CSP(세션 클라이언트/CSP 명).
  if (!st(fill.values.secretary_firm_name)) {
    try {
      const customer: any = sessionStore.getCustomerInfo?.();
      const nm = st(customer?.name) || (await fetchSessionClientNm(st(customer?.code)).catch(() => ''));
      if (nm) fill.setValue('secretary_firm_name', nm);
    } catch { /* ignore */ }
  }
  if (cd) {
    try {
      // ⚠ getActiveDirectors 는 배열이 아니라 { data: { directors } } — 예전엔 .map 에서 throw → catch 로 묻혀 화면 서명자가 비었다(PDF 는 생성 시 별도 조회라 정상).
      const res: any = await getActiveDirectors(cd);
      const dirs: any[] = Array.isArray(res) ? res : (res?.data?.directors || []);
      const names: string[] = dirs.map((d: any) => st(d.name || d.contactName || d.fullName || d.officerName)).filter(Boolean);
      if (names.length && !signersCtx.signersOf('dr_Signers').length) signersCtx.setSigners('dr_Signers', names.map((n: string) => mkSigner(n)));
    } catch { /* ignore */ }
  }
}

// Stage별 인물 lookup — 선임(f45_)=글로벌 CONTACT, 사임(res_)=엔티티 현직 DIRECTOR, Form 45 증인=CONTACT.
/** ✨ 조회를 요청한 칸 — 결과는 이 칸 기준으로 넣는다(조회 중 다른 칸 클릭에 영향받지 않게). */
let lookupKey = '';
fill.setActivateHandler((key: string) => {
  const bk = bareKey(key);
  if (bk === 'witness_name') {
    lookupKey = key;
    emit('request-person-lookup', { fieldGroup: 'awd-witness', scopedKey: key, defaultRole: 'CONTACT', lookupType: 'contact', subjectLabel: 'Witness (contact)' });
    return;
  }
  if (bk !== 'director_name') return;
  lookupKey = key;
  const sc = scopeOf(key);
  if (sc.startsWith('res_')) {
    emit('request-person-lookup', { fieldGroup: 'awd-resign', scopedKey: key, defaultRole: 'DIRECTOR', lookupType: 'officer', positionGroup: 'director', entityCd: entityCd.value, subjectLabel: 'Resigning director' });
  } else {
    emit('request-person-lookup', { fieldGroup: 'awd-appoint', scopedKey: key, defaultRole: 'CONTACT', lookupType: 'contact', subjectLabel: 'New director (contact)' });
  }
});
function applyPersonToActive(person: Record<string, any>) {
  const key = lookupKey || fill.activeKey.value; if (!key) return;
  lookupKey = '';
  const sc = scopeOf(key);
  const set = (k: string, val: unknown) => { const s = st(val); if (s) fill.setValue(k, s); };
  const fullName = person.name || person.contactName || person.fullName || `${person.firstName || ''} ${person.lastName || ''}`.trim();
  if (bareKey(key) === 'witness_name') {
    set(`${sc}witness_name`, fullName);
    set(`${sc}witness_address`, person.residentialAddress || person.address || person.primaryAddress);
    return;
  }
  set(`${sc}director_name`, fullName);
  set(`${sc}director_date_of_birth`, String(person.dateOfBirth || person.DATE_OF_BIRTH || person.dob || '').slice(0, 10));
  set(`${sc}director_identification_no`, person.identificationNo || person.contactRegNo || person.nricNo || person.idNo);
  set(`${sc}director_nationality`, person.nationalityName || person.nationality);
  set(`${sc}director_id_type`, person.idTypeName || person.idTypeCd || person.idType || person.identificationType);
  // 주소 — 컨택트 구조화 주소(contactData)에서 거주/연락 주소를 만든다(B32101 과 동일). 예전엔 alternateAddress
  //   같은 평면 키만 봐서 Contact address 가 비었다.
  const tmp = createEmptyAppointDirectorForm();
  applyDirectorStructuredProfileFromContactData(tmp, buildContactDataFromSelectedPerson(person as OfficerDetail));
  set(`${sc}director_address`, tmp.directorAddress || person.residentialAddress || person.address || person.primaryAddress);
  set(`${sc}director_alternate_address`, tmp.directorAlternateAddress || person.alternateAddress || person.contactAddress || person.serviceAddress);
  set(`${sc}director_email`, person.email || person.emailAddress || person.contactEmail);
  set(`${sc}director_mobile_no`, person.mobileNo || person.contactNo || person.mobile || person.phoneNo || person.mobileNumber);
  set(`${sc}director_country_cd`, tmp.directorMobileCountryCd || person.countryCd || person.mobileCountryCd || person.countryCode);
  set(`${sc}director_area_code`, tmp.directorMobileAreaCode || person.areaCode || person.mobileAreaCode);
  set(`${sc}director_contact_cd`, person.contactCd || person.CONTACT_CD || person.contactCode);
}
watch(() => props.selectedPerson, (p) => { if (p) applyPersonToActive(p as Record<string, any>); });

// 주소 버튼(📍) → 챗봇 주소 입력. 돌아온 값은 해당 스코프 키에 반영.
fill.setAddressHandler((key: string) => {
  const bk = bareKey(key);
  // 챗플로우(appointDirectorFlow) 가 인식하는 fieldGroup 으로 emit → directInput → address-input 위젯.
  const fg = bk === 'director_alternate_address' ? 'directorAlternateAddress' : 'directorAddress';
  const label = bk === 'director_alternate_address' ? 'Alternate address' : bk === 'company_address' ? 'Company address'
    : bk === 'witness_address' ? 'Witness address' : 'Director address';
  emit('request-address-lookup', { fieldGroup: fg, scopedKey: key, addresses: [], directInput: true, subjectLabel: label });
});
watch(() => props.selectedAddress, (sel) => {
  if (!sel?.value) return;
  const key = st(props.activeAddressContext?.scopedKey) || fill.activeKey.value;
  if (key) fill.setValue(key, sel.value);
});
watch(() => props.entityData, (ent) => { if (!editMode.value) void autofill(ent); }, { immediate: true, deep: true });

onMounted(async () => {
  try { const c = props.selection.task.TASK_CODE; if (c) { const cfg = await getTaskConfigByCode(c); taskConfigSteps.value = cfg?.processSteps || []; } } catch { /* ignore */ }
});

function gatherState(): Record<string, any> {
  return { values: { ...fill.exportPayload() }, signers: signersCtx.exportAll(), local: docRef.value?.gatherLocalState?.() };
}
function restoreState(s: any): void {
  if (!s) return;
  // 빈 값 먼저, 값 있는 것 나중 — 회사명·UEN 은 공유 키라 나중에 추가된 사본의 ''(f45_2/company_name)를
  //   마지막에 넣으면 모든 문서 회사명이 지워졌다(Edit 후 Update 시 Form 45·DR 회사명 빈칸).
  if (s.values) {
    const entries = Object.entries(s.values).filter(([, v]) => v != null).map(([k, v]) => [k, String(v)] as const);
    for (const [k, v] of entries) if (!v.trim()) fill.setValue(k, v);
    for (const [k, v] of entries) if (v.trim()) fill.setValue(k, v);
  }
  if (s.signers) signersCtx.importAll(s.signers);
  if (s.local) { autoLocal.value = s.local; docRef.value?.restoreLocalState?.(s.local); }
}
defineExpose({ gatherState, restoreState, createTask: handleCreateTask });

// 인물별 스코프에서 top-level 로 상속하면 안 되는 키(회사키 제외).
const OWNED = [
  'director_name', 'director_identification_no', 'director_nationality', 'director_id_type',
  'director_email', 'director_mobile_no', 'director_country_cd', 'director_area_code', 'director_contact_cd',
  'director_address', 'director_alternate_address', 'appointment_effective_date',
  'execution_date', 'resignation_date',
  // Form 45 (공통 최신본) — 생년월일·증인도 사본별
  'director_date_of_birth', 'witness_name', 'witness_address',
];
function scopedFormData(scope: string): Record<string, any> {
  const base = fill.exportPayload();
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(base)) { if (k.includes('/')) continue; if (OWNED.includes(bareKey(k))) continue; out[k] = v; }
  for (const [k, v] of Object.entries(base)) {
    if (!k.startsWith(scope)) continue;
    const bk = bareKey(k); const sv = st(v);
    if (sv === '' && bk in out && st(out[bk]) !== '') continue;
    out[bk] = v;
  }
  return out;
}
function execParts(iso: string) { const m = st(iso).match(/^(\d{4})-(\d{1,2})-(\d{1,2})/); return m ? { day: m[3].padStart(2, '0'), month: m[2].padStart(2, '0'), year: m[1] } : { day: '', month: '', year: '' }; }

/** Form 45 서명 블록 — 이름 아래 "Director" 직함(buildAppointedOfficerSignerJson 은 title 을 안 넣는다). */
function withDirectorTitle(json: string): string {
  if (!json) return json;
  try { return JSON.stringify((JSON.parse(json) as any[]).map((s) => ({ ...s, title: 'Director', showTitle: true }))); } catch { return json; }
}

/** 국가 calling code — COUNTRY_CD(B05xxx) 의 BIGO3(예 +65). Form 45 "Mobile phone number: +( )" 표시용(B32101 과 동일). */
const dialCache = new Map<string, string>();
async function dialCodeOf(countryCd: string): Promise<string> {
  const cc = st(countryCd);
  if (!cc) return '';
  if (dialCache.has(cc)) return dialCache.get(cc)!;
  let bigo3 = '';
  try {
    const res: any = await baseCodeApi.getCodeByTotalCode('CM', cc);
    const code: any = res?.code || res?.data?.code || res?.data || {};
    bigo3 = st(code.bigo3 || code.BIGO3);
  } catch { /* ignore */ }
  if (bigo3 && !bigo3.startsWith('+')) bigo3 = `+${bigo3}`;
  dialCache.set(cc, bigo3);
  return bigo3;
}

const isDrDoc = (n: string) => /DR\s*\(\s*Appoint\s*Withdraw\s+Director/i.test(String(n || ''));
const isForm45Doc = (n: string) => /Form\s*45/i.test(String(n || ''));
const isResignationDoc = (n: string) => /Resignation\s+Letter/i.test(String(n || ''));

async function handleCreateTask() {
  const title = effectiveTaskTitle.value;
  if (!title) { progress.value = 'Error: enter a Task Title in the chat first.'; return; }
  if (!props.entityData) { progress.value = 'Error: select an entity in the chat first.'; return; }
  const local = docRef.value?.gatherLocalState?.() || { newIds: [], resignIds: [] };
  const v = fill.exportPayload();
  // 이름 OR ID 번호 중 하나라도 있으면 유효한 행으로 인정 —
  // ID 만 입력한 상태(bizfile 에서 이름 자동 채움 유즈케이스) 도 통과시킴.
  const hasEntry = (scope: string, id: number) =>
    !!st(v[`${scope}_${id}/director_name`]) || !!st(v[`${scope}_${id}/director_identification_no`]);
  const news = (local.newIds || []).filter((id: number) => hasEntry('f45', id));
  const resigns = (local.resignIds || []).filter((id: number) => hasEntry('res', id));
  if (!news.length && !resigns.length) { progress.value = 'Error: add at least one director to appoint or withdraw.'; return; }
  const taskTypeCd = props.selection.task.TASK_CODE;
  if (!taskTypeCd) { progress.value = 'Error: Task code missing on preset.'; return; }

  creating.value = true;
  progress.value = 'Creating task...';
  try {
    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = { createDocument: steps.length === 0 ? true : steps.includes('doc'), requestTSign: steps.includes('t-sign'), proceedWithEFiling: steps.includes('e-filing') || steps.includes('efiling') };
    const companyBase = { company_name: st(v.company_name), company_registration_no: st(v.company_registration_no), company_address: st(v.company_address), secretary_firm_name: st(v.secretary_firm_name), resolution_date: st(v.resolution_date) };
    const taskFormData = { ...companyBase, presetForm: gatherState() };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id.'; creating.value = false; return; }
      await fetch(`/api/startTask/tasks/${taskId}/data`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ taskData: { formData: taskFormData } }) });
      try { await fetch(`/api/startTask/tasks/${taskId}/title`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ taskTitle: props.taskTitle }) }); } catch { /* optional */ }
    } else {
      const taskRes = await fetch('/api/startTask/tasks', { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ entityCd: entityCd.value || null, taskTypeCd, taskTypeName: props.selection.task.TASK_NAME, taskTitle: title, taskFlow, officerData: taskFormData }) });
      const taskJson = await taskRes.json();
      taskId = taskJson?.data?.taskId || taskJson?.taskId || null;
      if (!taskId) { progress.value = `Error: ${taskJson?.error?.message || taskJson?.message || 'Task creation failed'}`; creating.value = false; return; }
    }

    progress.value = 'Saving documents...';
    const drSigners = signersCtx.signersOf('dr_Signers');
    const drSignersJson = drSigners.length ? JSON.stringify(drSigners) : (await buildDirectorsSignersJson(entityCd.value || ''));
    const docsToSave: Array<Record<string, any>> = [];
    for (const d of (props.selection.documents || [])) {
      const tid = Number(d?.tmpId || 0); if (!tid) continue;
      const name = String(d.templateName || d.docCode || '');
      if (isDrDoc(name)) {
        const drFd: Record<string, any> = {
          ...companyBase,
          new_directors: news.map((id: number) => ({ director_name: st(v[`f45_${id}/director_name`]), director_identification_no: st(v[`f45_${id}/director_identification_no`]), director_id_label: directorIdLabel(v[`f45_${id}/director_id_type`], v[`f45_${id}/director_identification_no`]) })),
          resign_directors: resigns.map((id: number) => ({ director_name: st(v[`res_${id}/director_name`]), director_identification_no: st(v[`res_${id}/director_identification_no`]), director_id_label: directorIdLabel(v[`res_${id}/director_id_type`], v[`res_${id}/director_identification_no`]) })),
          signers: drSignersJson, Signers: drSignersJson,
        };
        docsToSave.push(buildSgPresetPrepareDocumentItem({ templateId: tid, templateName: name, personName: st(v.company_name) || 'Company', commonFormData: drFd, entityCd: entityCd.value || undefined }));
      } else if (isForm45Doc(name)) {
        for (const id of news) {
          const fd = scopedFormData(`f45_${id}/`);
          const ex = execParts(st(fd.execution_date)); fd.execution_day = ex.day; fd.execution_month = ex.month; fd.execution_year = ex.year;
          // Form 45 "Mobile phone number: +( )" — 국가번호 + 지역 + 번호 (문서 표시만, e-Filing 은 원본 키 사용)
          const dial = await dialCodeOf(st(fd.director_country_cd));
          fd.director_mobile_no = [dial, st(fd.director_area_code), st(fd.director_mobile_no)].filter(Boolean).join(' ');
          const pn = st(fd.director_name);
          const sj = withDirectorTitle(buildAppointedOfficerSignerJson({ name: pn, role: 'director', contactCd: st(fd.director_contact_cd), contactRegNo: st(fd.director_identification_no) }));
          fd.signers = sj; fd.Signers = sj;
          const label = news.length > 1 ? `${name} — ${pn || `Director ${id}`}` : name;
          docsToSave.push(buildSgPresetPrepareDocumentItem({ templateId: tid, templateName: label, personName: pn, commonFormData: fd, entityCd: entityCd.value || undefined }));
        }
      } else if (isResignationDoc(name)) {
        resigns.forEach((id: number) => {
          const fd = scopedFormData(`res_${id}/`);
          const pn = st(fd.director_name);
          const sj = buildAppointedOfficerSignerJson({ name: pn, role: 'director', contactRegNo: st(fd.director_identification_no) });
          fd.signers = sj; fd.Signers = sj;
          const label = resigns.length > 1 ? `${name} — ${pn || `Director ${id}`}` : name;
          docsToSave.push(buildSgPresetPrepareDocumentItem({ templateId: tid, templateName: label, personName: pn, commonFormData: fd, entityCd: entityCd.value || undefined }));
        });
      }
    }

    if (docsToSave.length === 0) {
      // 해임 미입력 시 문서 미생성 방어 로그 —
      //   B32111 preset 에 템플릿이 실려있어도 docsToSave 가 비어 나오면 원인이
      //   (a) selection.documents 자체 비어있음, (b) 템플릿 name 이 isDrDoc/isForm45Doc/
      //   isResignationDoc 정규식과 매칭 안 됨, (c) news/resigns 둘 다 비어있음 중 하나.
      const templateNames = (props.selection.documents || []).map((d: any) => String(d?.templateName || d?.docCode || ''));
      console.warn(
        '[AppointWithdrawDirector] docsToSave EMPTY — 태스크는 생성됐지만 문서가 저장되지 않았습니다.',
        { presetDocs: templateNames, newsCount: news.length, resignsCount: resigns.length },
      );
      progress.value = `Warning: 태스크는 생성됐지만 저장할 문서가 없습니다 (템플릿 매칭 실패 또는 프리셋에 문서가 없음). templates=${templateNames.join(', ') || '(none)'}`;
    } else {
      try {
        const r = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
        }).then((x) => x.json());
        if (!r?.success) {
          progress.value = `Documents save failed: ${r?.message || 'unknown'}`;
          console.error('[AppointWithdrawDirector] prepare-documents returned failure', r);
        }
      } catch (err) {
        progress.value = `Documents save failed: ${err instanceof Error ? err.message : String(err)}`;
        console.error('[AppointWithdrawDirector] prepare-documents threw', err);
      }
    }
    try { await fetch(`/api/startTask/tasks/${taskId}/document-status`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include' }); } catch { /* ignore */ }
    try { await fetch('/api/startTask/preset/use-log', { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }) }); } catch { /* ignore */ }
    emit('task-created', { taskId, taskTitle: title });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (err: unknown) {
    progress.value = `Error: ${err instanceof Error ? err.message : String(err)}`;
  } finally { creating.value = false; }
}
</script>

<template>
  <div class="awd-panel" :class="{ studio }">
    <div v-if="!studio" class="co-head">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME || 'Appoint & Withdraw Director' }}</div>
        <div class="co-subtitle">Director&rsquo;s resolution — Appoint &amp; Withdraw director (Singapore)</div>
      </div>
      <div class="co-actions">
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-task-title')">Change title</v-btn>
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-entity')">Change company</v-btn>
      </div>
    </div>
    <div class="co-doc"><AppointWithdrawPackageDoc ref="docRef" :auto-local="autoLocal" /></div>
    <div v-if="!studio" class="co-foot">
      <v-btn color="primary" class="text-none" :loading="creating" :disabled="!entityData || !effectiveTaskTitle || creating" @click="handleCreateTask">
        {{ editMode ? 'Update task' : 'Create Task' }}
      </v-btn>
      <span v-if="progress && !creating" class="co-err">{{ progress }}</span>
    </div>
    <div v-else-if="progress && !creating" class="co-foot"><span class="co-err">{{ progress }}</span></div>
  </div>
</template>

<style scoped>
.awd-panel { display: flex; flex-direction: column; gap: 12px; min-height: 0; }
.awd-panel.studio .co-foot { position: sticky; bottom: 0; background: #fff; padding: 10px 0; z-index: 2; }
.co-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.co-title { font-size: 16px; font-weight: 800; color: #1a2336; }
.co-subtitle { font-size: 12px; color: #6b7a94; margin-top: 2px; }
.co-actions { display: flex; gap: 4px; flex-shrink: 0; }
.co-doc { flex: 1; min-height: 0; }
.co-foot { display: flex; align-items: center; gap: 12px; }
.co-err { font-size: 12px; color: #c0392b; }
</style>
